import {DataType} from "../model/DataType.model";
import {ValidationType} from "../model/ValidationType.model";
import {Documentation, DocumentationDescription} from "../model/Documentation.model";
import {ComponentElementProperty} from "../component_element/ComponentElementManifest.model";

/**
 * 폼 데이터 스키마 모델
 * @since 0.0.1
 * @author Heungsoo Ham
 * @interface
 */
export interface FormDataSchema {
    version?: string;
    isPublished?: boolean;
    globalOption?: {
        condition?: {
            description?: string;
            readonly?: string;
            printable?: string;
        };
        componentLayout?: {
            marginTop?: string;
            marginRight?: string;
            marginBottom?: string;
            marginLeft?: string;
            paddingTop?: string;
            paddingRight?: string;
            paddingBottom?: string;
            paddingLeft?: string;
        };
        theme?: {
            name?: string;
            baseFontSize?: string;
        };
        customComponent?: Array<CustomComponent>;
        externalResource?: {
            css?: Array<string>;
            js?: Array<string>;
        };
    };
    schema: FormSchema;
    relationship?: {
        [key: string]: FormRelationship;
    };
    pvMap?: EntityProcessVariableMap;
}

export interface CustomComponent {
    name?: string;
    version?: string;
}

export interface FormSchema {
    version?: string;
    structure?: FormStructure;
    entity: {
        [key: string]: FormField;
    };
    group?: {
        [key: string]: FormGroup;
    };
    grid?: {
        [key: string]: GridAliasAndPipe;
    };
    alias?: {
        [key: string]: string;
    };
}

/**
 * 폼 스트럭처 정의 모델
 */
export interface FormStructure {
    [key: string]: {
        group?: boolean;
        child?: Array<string>;
    };
}

/**
 * 폼 그룹 데이터 정의 모델
 */
export interface FormGroup extends Documentation, DocumentationDescription {
    isGrid?: boolean;
    inline?: boolean;
    grid?: {
        alias?: string;
        create?: string;
        update?: string;
        list?: string;
        delete?: string;
        check?: string;
        retrieve?: string;
        retrieveUrl?: string;
        convertAlias?: boolean;
        hook?: GridHook;
    };
}

export interface GridHook {
    beforeCreate?: string;
    afterCreate?: string;
    beforeUpdate?: string;
    afterUpdate?: string;
    beforeDelete?: string;
    afterDelete?: string;
    beforeDetail?: string;
    afterDetail?: string;
    beforeCustom?: string;
    afterCustom?: string;
    beforeList?: string;
    afterList?: string;
    beforeRetrieve?: string;
    afterRetrieve?: string;
}

export interface GridAliasAndPipe {
    [key: string]: {
        alias: string;
        inPipe?: string;
        outPipe?: string;
    };
}

export interface InValidFormFieldTypeCheck {
    /** 폼을 그릴 당시의  type 과 Project Schema Entity Type 가 다를 수 있다 => 모델러에서 타입 변경 시, 따라서, 프로젝트내 타입과 그릴 당시의 type 이 다른지 확인 용 */
    projectType?: DataType;
}

export enum BuiltInGridType {
    PK = "PrimaryKey",
    EPK = "ExternalPrimaryKey",
    CUSER = "createUser",
    LUSER = "lastUpdateUser",
    CDATE = "createDate",
    LDATE = "lastUpdateDate"
}

export enum GridSystemEntityName {
    PK = "_pk",
    EPK = "_epk",
    CUSER = "_creator",
    LUSER = "_modifier",
    CDATE = "_createDate",
    LDATE = "_modifyDate"
}

export const BuiltInGridEntity: Array<FormField> = [
    {
        name: "Primary Key",
        label: "Primary Key",
        type: DataType.STRING,
        readonly: true,
        builtInGridType: BuiltInGridType.PK
    },
    {
        name: "External Primary Key",
        label: "External Primary Key",
        type: DataType.STRING,
        readonly: true,
        builtInGridType: BuiltInGridType.EPK
    },
    {
        name: "Creator",
        label: "Creator",
        type: DataType.USER,
        readonly: true,
        builtInGridType: BuiltInGridType.CUSER
    },
    {
        name: "Modifier",
        label: "Modifier",
        type: DataType.USER,
        readonly: true,
        builtInGridType: BuiltInGridType.LUSER
    },
    {
        name: "Created Date",
        label: "Created Date",
        type: DataType.DATETIME,
        readonly: true,
        builtInGridType: BuiltInGridType.CDATE
    },
    {
        name: "Modified Date",
        label: "Modified Date",
        type: DataType.DATETIME,
        readonly: true,
        builtInGridType: BuiltInGridType.LDATE
    }
];

export interface FormField extends Documentation, DocumentationDescription {
    label?: string;
    type?: DataType;
    /** String 등 길이가 필요할 때 */
    dataLength?: number;
    /** editable 불가 */
    readonly?: boolean;
    component?: string;
    componentProperty?: ComponentElementProperty;
    validation?: ValidationType;
    lookup?: {
        id?: string;
        parent?: string;
        lazy?: boolean;
        rawDataSrcId?: string;
    };
    file?: {
        min?: {
            size: number;
            unit: string;
        };
        max?: {
            size: number;
            unit: string;
        };
        upload?: string;
        update?: string;
        list?: string;
        delete?: string;
        hasDescription?: boolean;
        extension?: {
            prevent: boolean;
            extension: Array<string>;
        }
    };
    transient?: boolean;
    builtInGridType?: BuiltInGridType;
    /** 폼 스키마 유효성 관련 */
    invalidTypeCheck?: InValidFormFieldTypeCheck;
}

/**
 * 폼 데이터간 관계 정의 모델
 */
export interface FormRelationship extends Documentation, DocumentationDescription {
    source: FormSchemaAlias;
    target: FormSchemaAlias;
    mapping: Array<FormRelationshipMapping>;
    validation: Array<FormRelationshipValidation>;
}

export interface FormRelationshipValidation extends Documentation, DocumentationDescription {
    /**
     * target 컴퍼넌트에서 밸리데이션 메세지 출력
     * 예를 들어 출발일과 종료일이 관계를 맺고 있을 때, 종료일이 출발일보다 앞서면
     * messageTarget 은 종료일 컴퍼넌트의 id가 되고, 밸리데이션을 통과 못했을 경우 해당 메세지가 해당 컴퍼넌트에 출력된다
     */
    messageTarget: string;
    /**
     * 관계된 데이터 값이 수정될 경우
     * 펑션을 호출해 주고, 각 파라미터로 값을 넘겨 줌.
     * 밸리데이션 통과시 null 을 통과 못했을 경우 메세지를 리턴.
     * 메세지가 $i8n  로 시작하면 해당 메세지를 출력
     */
    function: string;
}

export interface FormRelationshipMapping extends Documentation, DocumentationDescription {
    calculation: string;
    target: string;
}


/**
 * source, target
 */
export interface FormSchemaAlias {
    [key: string]: string;
}

/**
 * source, target
 */
export enum FormMapperPrefix {
    SOURCE = "source",
    TARGET = "target"
}

/**
 * Entity & Process variable map
 * @since 0.0.1
 * @author Jongkwang Park
 * @interface
 */
export interface EntityProcessVariableMap {
    structure: Array<EntityProcessVariablePair>;
    procVar: {
        /* key: The name of the process variable */
        [key: string]: ProcessVariableModel;
    };
}

export interface EntityProcessVariablePair extends DocumentationDescription {
    entityId: string; /* mapped entity's Id */
    pvName: string; /* process variable name */
}

export interface ProcessVariableModel {
    pvName: string; /* process variable name */
    pvType: ProcessVariableType; /* process variable type */
    pdId: string; /* process definition Id */
}

/**
 * Process Variable Type of BPM Server
 * @since 0.0.1
 * @author Jongkwang Park
 * @enum
 */
export enum BpmProcessVariableType {
    A = "Application",
    B = "Application Array",
    X = "Complex",
    Y = "Complex Array",
    L = "Custom",
    D = "Date",
    E = "Date Array",
    F = "File",
    G = "File Array",
    N = "Number",
    O = "Number Array",
    P = "Participant",
    Q = "Participant Array",
    S = "String",
    T = "String Array"
}

/**
 * Process Variable Type
 * AppDev에서는 BPM의 process variable type중에서 date, number, string & 이들의 array만 사용한다.
 * @since 0.0.1
 * @author Jongkwang Park
 * @enum
 */
export enum ProcessVariableType {
    D = "Date",
    E = "Date Array",
    N = "Number",
    O = "Number Array",
    S = "String",
    T = "String Array"
}
